/**
 * @ai-content-hub/content-prompt — i18n
 *
 * Self-contained translations for prompt form, chat, and upload UI.
 * Supports `en` and `vi`. The active locale is set globally via `setPromptLocale()`
 * or per-component via the `language` prop.
 */

export interface PromptTranslations {
    // DefaultPromptForm
    prompt: string;
    promptPlaceholder: string;
    generate: string;
    regenerate: string;
    generating: string;
    retry: string;
    cancel: string;

    // DocumentList / UploadButton
    uploadDocument: string;
    removeDocument: string;
    uploadedDocuments: string;

    // ChatLayout
    rollingBack: string;
    generationFailed: string;
    generationComplete: string;

    // ChatMessage
    rollbackConfirmTitle: string;
    rollbackConfirmMessage: string;
    rollback: string;

    // EAA-2031: form chrome that used to be hard-coded English.
    questionSetup: string;
    // EAA-2031: description card heading, previously hard-coded 'Description' / '(Optional)'.
    descriptionLabel: string;
    optionalSuffix: string;
    media: string;
    mediaNone: string;
    mediaPassage: string;
    mediaImage: string;
    mediaAudio: string;
    numQuestions: string;
    numOptions: string;
    optionsPerQuestion: string;
    correctPerQuestion: string;
    singleChoiceOneCorrect: string;
    correctMin: string;
    correctMax: string;
    numBlanks: string;
    numChoices: string;
    wordBankSize: string;
    distractorsPerBlank: string;
    leftItems: string;
    rightItems: string;
    numZones: string;
    numItems: string;
    maxWords: string;
    minWords: string;
    createQuestions: string;
    creating: string;
    jobPending: string;
    jobRunning: string;
    jobCompleted: string;
    jobFailed: string;
    jobCancelled: string;
    jobContinue: string;
    jobDismiss: string;
    /** EAA-2026: heading of the trailing group of types in no tenant question-type group. */
    questionTypeGroupOther: string;
    /** EAA-2026: Quick Create search + collapsible groups. */
    questionTypeSearchPlaceholder: string;
    questionTypeClearSearch: string;
    questionTypeNoResults: string;
    questionTypeExpandAll: string;
    questionTypeCollapseAll: string;
    /** EAA-2026: bulk settings — compound field labels and the unit captions inside them. */
    bulkBlanksGroup: string;
    bulkMatchingGroup: string;
    bulkCorrectRange: string;
    bulkWordRange: string;
    unitBlanks: string;
    unitChoices: string;
    unitDistractors: string;
    unitLeft: string;
    unitRight: string;
    unitZones: string;
    unitItems: string;
}

const en: PromptTranslations = {
    prompt: 'Prompt',
    promptPlaceholder: 'Describe the content you want to generate...',
    generate: 'Generate',
    regenerate: 'Regenerate',
    generating: 'Generating...',
    retry: 'Retry',
    cancel: 'Cancel',

    uploadDocument: 'Upload document',
    removeDocument: 'Remove document',
    uploadedDocuments: 'Uploaded documents',

    rollingBack: 'Rolling back...',
    generationFailed: 'Generation failed',
    generationComplete: 'Generation complete',

    rollbackConfirmTitle: 'Rollback to version {version}?',
    rollbackConfirmMessage:
        'The current content will be archived and replaced with version {version}. You can rollback again later if needed.',
    rollback: 'Rollback',
    questionSetup: 'Question setup',
    descriptionLabel: 'Description',
    optionalSuffix: '(Optional)',
    media: 'Media',
    mediaNone: 'None',
    mediaPassage: 'Passage',
    mediaImage: 'Image',
    mediaAudio: 'Audio',
    numQuestions: 'Number of questions',
    numOptions: 'Options',
    optionsPerQuestion: 'Options per question',
    correctPerQuestion: 'Correct answers per question',
    singleChoiceOneCorrect: 'Single choice always has exactly one correct answer.',
    correctMin: 'Min correct answers',
    correctMax: 'Max correct answers',
    numBlanks: 'Number of blanks',
    numChoices: 'Choices per blank',
    wordBankSize: 'Word bank size',
    distractorsPerBlank: 'Distractors per blank',
    leftItems: 'Left items',
    rightItems: 'Right items',
    numZones: 'Zones',
    numItems: 'Number of items',
    maxWords: 'Max words',
    minWords: 'Min words',
    createQuestions: 'Create questions',
    creating: 'Creating...',
    jobPending: 'Pending',
    jobRunning: 'Generating...',
    jobCompleted: 'Complete',
    jobFailed: 'Generation failed',
    jobCancelled: 'Cancelled',
    jobContinue: 'Continue',
    jobDismiss: 'Dismiss',
    questionTypeGroupOther: 'Others',
    questionTypeSearchPlaceholder: 'Search question types (name, code, English)…',
    questionTypeClearSearch: 'Clear search',
    questionTypeNoResults: 'No matching question types',
    questionTypeExpandAll: 'Expand all',
    questionTypeCollapseAll: 'Collapse all',
    bulkBlanksGroup: 'Blanks',
    bulkMatchingGroup: 'Matching',
    bulkCorrectRange: 'Correct answers',
    bulkWordRange: 'Word count',
    unitBlanks: 'blanks',
    unitChoices: 'choices',
    unitDistractors: 'distractors',
    unitLeft: 'left',
    unitRight: 'right',
    unitZones: 'zones',
    unitItems: 'items',
};

const vi: PromptTranslations = {
    prompt: 'Mô tả',
    promptPlaceholder: 'Mô tả nội dung bạn muốn tạo...',
    generate: 'Tạo',
    regenerate: 'Tạo lại',
    generating: 'Đang tạo...',
    retry: 'Thử lại',
    cancel: 'Hủy',

    uploadDocument: 'Tải lên tài liệu',
    removeDocument: 'Xóa tài liệu',
    uploadedDocuments: 'Tài liệu đã tải lên',

    rollingBack: 'Đang khôi phục...',
    generationFailed: 'Tạo thất bại',
    generationComplete: 'Tạo hoàn tất',

    rollbackConfirmTitle: 'Khôi phục về phiên bản {version}?',
    rollbackConfirmMessage:
        'Nội dung hiện tại sẽ được lưu trữ và thay thế bằng phiên bản {version}. Bạn có thể khôi phục lại sau.',
    rollback: 'Khôi phục',
    questionSetup: 'Thiết lập câu hỏi',
    descriptionLabel: 'Mô tả',
    optionalSuffix: '(Không bắt buộc)',
    media: 'Phương tiện',
    mediaNone: 'Không có',
    mediaPassage: 'Đoạn văn',
    mediaImage: 'Hình ảnh',
    mediaAudio: 'Âm thanh',
    numQuestions: 'Số câu hỏi',
    numOptions: 'Số lựa chọn',
    optionsPerQuestion: 'Số lựa chọn mỗi câu',
    correctPerQuestion: 'Số đáp án đúng mỗi câu',
    singleChoiceOneCorrect: 'Chọn một đáp án luôn có đúng một đáp án đúng.',
    correctMin: 'Số đáp án đúng tối thiểu',
    correctMax: 'Số đáp án đúng tối đa',
    numBlanks: 'Số chỗ trống',
    numChoices: 'Số lựa chọn mỗi chỗ trống',
    wordBankSize: 'Số từ trong ngân hàng từ',
    distractorsPerBlank: 'Số phương án nhiễu mỗi chỗ trống',
    leftItems: 'Số mục cột trái',
    rightItems: 'Số mục cột phải',
    numZones: 'Số vùng',
    numItems: 'Số mục',
    maxWords: 'Số từ tối đa',
    minWords: 'Số từ tối thiểu',
    createQuestions: 'Tạo câu hỏi',
    creating: 'Đang tạo...',
    jobPending: 'Đang chờ',
    jobRunning: 'Đang tạo...',
    jobCompleted: 'Hoàn tất',
    jobFailed: 'Tạo thất bại',
    jobCancelled: 'Đã hủy',
    jobContinue: 'Tiếp tục',
    jobDismiss: 'Đóng',
    questionTypeGroupOther: 'Khác',
    questionTypeSearchPlaceholder: 'Tìm dạng câu hỏi (tên, mã, tiếng Anh)…',
    questionTypeClearSearch: 'Xoá tìm kiếm',
    questionTypeNoResults: 'Không tìm thấy dạng câu hỏi phù hợp',
    questionTypeExpandAll: 'Mở rộng tất cả',
    questionTypeCollapseAll: 'Thu gọn tất cả',
    bulkBlanksGroup: 'Chỗ trống',
    bulkMatchingGroup: 'Ghép nối',
    bulkCorrectRange: 'Số đáp án đúng',
    bulkWordRange: 'Số từ',
    unitBlanks: 'chỗ',
    unitChoices: 'lựa chọn',
    unitDistractors: 'phương án nhiễu',
    unitLeft: 'vế trái',
    unitRight: 'vế phải',
    unitZones: 'vùng',
    unitItems: 'mục',
};

const translations: Record<string, PromptTranslations> = { en, vi };

let currentLocale = 'en';

/** Set the global locale for all prompt components. */
export function setPromptLocale(locale: string): void {
    const normalized = locale?.toLowerCase().startsWith('vi') ? 'vi' : 'en';
    currentLocale = normalized;
}

/** Get the current global locale. */
export function getPromptLocale(): string {
    return currentLocale;
}

/** Get translations for the given locale (or current global locale). */
export function getPromptTranslations(locale?: string): PromptTranslations {
    if (locale) {
        const normalized = locale.toLowerCase().startsWith('vi') ? 'vi' : 'en';
        return translations[normalized] || translations.en;
    }
    return translations[currentLocale] || translations.en;
}

/** React hook — returns translations for the current or given locale. */
export function usePromptTranslations(locale?: string): PromptTranslations {
    return getPromptTranslations(locale);
}

/** EAA-2031: media option value → translated label (unknown values fall back to the value). */
export function mediaOptionLabel(value: string, t: PromptTranslations): string {
    const labels: Record<string, string> = {
        none: t.mediaNone,
        passage: t.mediaPassage,
        image: t.mediaImage,
        audio: t.mediaAudio,
    };
    return labels[value] ?? value;
}
